import {
	MetadataInformation,
	MetadataMagnification,
	MetadataPageLayout,
	Parameter
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import ZoomMode from "../viewer/settings/ZoomMode";
import PageViewMode from "../viewer/settings/PageViewMode";
import MouseMode from "../viewer/settings/MouseMode";
import MouseFunction from "../viewer/settings/MouseFunction";
import PortalDocument from "./PortalDocument";

export interface ViewerSettingsInterface {
	pagePadding: boolean;
	continuous: boolean;
	pageCover: boolean;
	zoomValue: number;
	zoomMode: ZoomMode;
	pageMode: PageViewMode;
	mouseMode: MouseMode;
	mouseFunction: MouseFunction;
	currentPage: number;
	mouseFunctionActive: boolean;
}

export default class ViewerSettings implements ViewerSettingsInterface, Parameter {
	private _pagePadding: boolean;
	private _continuous: boolean;
	private _pageCover: boolean;
	private _zoomValue: number;
	private _zoomMode: ZoomMode;
	private _pageMode: PageViewMode;
	private _mouseMode: MouseMode;
	private _mouseFunction: MouseFunction;
	private _currentPage: number;
	private _mouseFunctionActive: boolean;

	/**
	 * Constructs a {@link ViewerSettings} instance
	 */
	constructor(data?: any) {
		this._pagePadding = FunctionHelper.exists(data?.pagePadding) ? data.pagePadding : true;
		this._continuous = FunctionHelper.exists(data?.continuous) ? data.continuous : false;
		this._pageCover = FunctionHelper.exists(data?.pageCover) ? data.pageCover : false;
		this._zoomValue = FunctionHelper.exists(data?.zoomValue) ? data.zoomValue : 100;
		this._zoomMode = FunctionHelper.exists(data?.zoomMode) ? data.zoomMode : ZoomMode.PAGE;
		this._pageMode = FunctionHelper.exists(data?.pageMode) ? data.pageMode : PageViewMode.SINGLE;
		this._mouseMode = FunctionHelper.exists(data?.mouseMode) ? data.mouseMode : MouseMode.MOVE;
		this._mouseFunction = FunctionHelper.exists(data?.mouseFunction) ? data.mouseFunction : MouseFunction.NONE;
		this._currentPage = FunctionHelper.exists(data?.currentPage) ? data.currentPage : 1;
		this._mouseFunctionActive = FunctionHelper.exists(data?.mouseFunctionActive) ? data.mouseFunctionActive : false;
	}

	public static fromDocument(portalDocument?: PortalDocument): ViewerSettings {
		let result = new ViewerSettings();

		let initialPage: number | undefined = FunctionHelper.applyValue(
			portalDocument?.metadata?.information?.initialPage, MetadataInformation.getInitialPageDefault()
		);
		if (FunctionHelper.exists(initialPage)) {
			result._currentPage = initialPage!;
		}

		let magnification: MetadataMagnification | undefined = FunctionHelper.applyValue(
			portalDocument?.metadata?.information?.magnification, MetadataInformation.getMagnificationDefault()
		);
		switch (magnification) {
			case MetadataMagnification.FitHeight:
				result._zoomMode = ZoomMode.HEIGHT;
				break;
			case MetadataMagnification.FitActual:
				result._zoomMode = ZoomMode.ORIGINAL;
				break;
			case MetadataMagnification.Zoom:
				result._zoomMode = ZoomMode.CUSTOM;
				result._zoomValue = FunctionHelper.applyValue(
					portalDocument?.metadata?.information?.magnificationZoom, result._zoomValue
				);
				break;
			case MetadataMagnification.FitWidth:
				result._zoomMode = ZoomMode.WIDTH;
				break;
		}

		let pageLayout: MetadataPageLayout | undefined = FunctionHelper.applyValue(
			portalDocument?.metadata?.information?.pageLayout, MetadataInformation.getPageLayoutDefault()
		);
		switch (pageLayout) {
			case MetadataPageLayout.OneColumn:
				result._continuous = true;
				break;
			case MetadataPageLayout.TwoColumnLeft:
				result._pageMode = PageViewMode.DUAL;
				result._continuous = true;
				break;
			case MetadataPageLayout.TwoColumnRight:
				result._pageMode = PageViewMode.DUAL;
				result._continuous = true;
				result._pageCover = true;
				result._pagePadding = false;
				break;
			case MetadataPageLayout.TwoPageLeft:
				result._pageMode = PageViewMode.DUAL;
				break;
			case MetadataPageLayout.TwoPageRight:
				result._pageMode = PageViewMode.DUAL;
				result._pageCover = true;
				result._pagePadding = false;
				break;
		}

		return result;
	}

	/**
	 * sets current page to the first page
	 */
	public firstPage(): void {
		this._currentPage = 1;
	}

	/**
	 * sets current page to the next page or next page block for dual page mode
	 */
	public nextPage(pageCount: number, pageMode?: PageViewMode): void {
		if (FunctionHelper.applyValue(pageMode, this._pageMode) === PageViewMode.DUAL &&
			this._currentPage + 2 <= pageCount) {
			this._currentPage += 2;
		} else if (this._currentPage + 1 <= pageCount) {
			this._currentPage += 1;
		}
	}

	/**
	 * sets current page to the last page
	 */
	public lastPage(pageCount: number): void {
		this._currentPage = pageCount;
	}

	/**
	 * sets current page to the previous page or previous page block for dual page mode
	 */
	public previousPage(pageMode?: PageViewMode): void {
		if (FunctionHelper.applyValue(pageMode, this._pageMode) === PageViewMode.DUAL && this._currentPage - 2 >= 1) {
			this._currentPage -= 2;
		} else if (this._currentPage - 1 >= 1) {
			this._currentPage -= 1;
		}
	}

	get pagePadding(): boolean {
		return this._pagePadding;
	}

	set pagePadding(value: boolean) {
		this._pagePadding = value;
	}

	get continuous(): boolean {
		return this._continuous;
	}

	set continuous(value: boolean) {
		this._continuous = value;
	}

	get pageCover(): boolean {
		return this._pageCover;
	}

	set pageCover(value: boolean) {
		this._pageCover = value;
	}

	get zoomValue(): number {
		return this._zoomValue;
	}

	set zoomValue(value: number) {
		this._zoomValue = value;
	}

	get zoomMode(): ZoomMode {
		return this._zoomMode;
	}

	set zoomMode(value: ZoomMode) {
		this._zoomMode = value;
	}

	get pageMode(): PageViewMode {
		return this._pageMode;
	}

	set pageMode(value: PageViewMode) {
		this._pageMode = value;
	}

	get mouseMode(): MouseMode {
		return this._mouseMode;
	}

	set mouseMode(value: MouseMode) {
		this._mouseMode = value;
	}

	get mouseFunction(): MouseFunction {
		return this._mouseFunction;
	}

	set mouseFunction(value: MouseFunction) {
		this._mouseFunction = value;
	}

	get mouseFunctionActive(): boolean {
		return this._mouseFunctionActive;
	}

	set mouseFunctionActive(value: boolean) {
		this._mouseFunctionActive = value;
	}

	get currentPage(): number {
		return this._currentPage;
	}

	set currentPage(value: number) {
		this._currentPage = value;
	}

	public static fromJson(data: any): ViewerSettings {
		if (!FunctionHelper.exists(data)) {
			return data;
		}

		return new ViewerSettings(data);
	}

	public toJson(): any {
		return {
			'pagePadding': this.pagePadding,
			'continuous': this.continuous,
			'pageCover': this.pageCover,
			'zoomValue': this.zoomValue,
			'zoomMode': this.zoomMode,
			'pageMode': this.pageMode,
			'mouseMode': this.mouseMode,
			'mouseFunction': this.mouseFunction,
			'currentPage': this.currentPage,
			'mouseFunctionActive': this.mouseFunctionActive
		};
	}

	public clone(): ViewerSettings {
		return ViewerSettings.fromJson(this.toJson());
	}
}